import React, { useEffect, useRef, useState } from 'react'
import BigModal from '../../../components/Modals/BigModal'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MFALogin from '../../../helpers/auth/MFALogin'
import API from '../../../services/API'
import AnDateField from '../../../components/DateTime/DateField'
import Demographics from './Demographics'
import Mobility from './Mobility'
import Clinical from './Clinical'
import SubmitForm from './Submit'
import moment from 'moment'
import ErrorModal from '../../../components/Modals/Error'
import { TravelExploreTwoTone } from '@mui/icons-material'
import Loading from '../../loading/loadingPage'

export const Section = ({ title, subtitle, children }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 4,
      border: '1px solid',
      borderColor: 'divider',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>

      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>

    <CardContent sx={{ p: 3 }}>
      <Grid container spacing={2.5}>
        {children}
      </Grid>
    </CardContent>
  </Card>
)

const AddNewPatient = ({
  open,
  onClose,
  actionAfterSubmit,
  existingPatient,
  noTitle,
  portalMode = false,
  portalOrganisation = null,
}) => {
  const [requiresMFA, setRequiresMFA] = useState(null)
  const [firstName, setFirstName] = useState(existingPatient?.firstName ?? '')
  const [lastName, setLastName] = useState(existingPatient?.lastName ?? '')
  const [dob, setDOB] = useState(existingPatient?.dob ?? null)
  const [gender, setGender] = useState(existingPatient?.gender ?? '')
  const [gpSurgery, setGpSurgery] = useState(existingPatient?.gpSurgery ?? '')
  const [nhsNumber, setNHSNumber] = useState(existingPatient?.nhsNumber ?? '')
  const [hospitalNumbers, setHospitalNumbers] = useState(
    existingPatient?.hospitalNumbers ?? [],
  )
  const [addressLine1, setAddressLine1] = useState(
    existingPatient?.addressLine1 ?? '',
  )
  const [addressLine2, setAddressLine2] = useState(
    existingPatient?.addressLine2 ?? '',
  )
  const [town, setTown] = useState(existingPatient?.town ?? '')
  const [city, setCity] = useState(existingPatient?.city ?? '')
  const [postCode, setPostCode] = useState(existingPatient?.postCode ?? '')
  const [location, setLocation] = useState(existingPatient?.location)
  const [addressMeta, setAddressMeta] = useState(existingPatient?.addressMeta)
  const [email, setEmail] = useState(existingPatient?.email ?? '')
  const [phoneNumber1, setPhoneNumber1] = useState(
    existingPatient?.phoneNumber1 ?? '',
  )
  const [phoneNumber2, setPhoneNumber2] = useState(
    existingPatient?.phoneNumber2 ?? '',
  )
  const [nokName, setNOKName] = useState(existingPatient?.nokName ?? '')
  const [nokRelationship, setNokRelationship] = useState(
    existingPatient?.nokRelationship ?? '',
  )
  const [nokContactNumber, setNokContactNumber] = useState(
    existingPatient?.nokContactNumber ?? '',
  )
  const [mobility, setMobility] = useState(
    existingPatient?.mobility ?? {
      category: '',
      isBariatric: false,
      mustTravelAlone: false,
      aids: [],
      heightCm: '',
      weightKg: '',
    },
  )
  const [clinicalNotes, setClinicalNotes] = useState(
    existingPatient?.clinicalNotes ?? '',
  )
  const [riskFlags, setRiskFlags] = useState(existingPatient?.riskFlags ?? [])
  const [allergies, setAllergies] = useState(existingPatient?.allergies ?? [])
  const [medications, setMedications] = useState(
    existingPatient?.medications ?? [],
  )
  const [pmhx, setPmhx] = useState(existingPatient?.pmhx ?? [])
  const [changesMade, setChangesMade] = useState({})
  const [addressUpdated, setAddressUpdated] = useState(false)

  useEffect(() => {
    if (!open) return

    setRequiresMFA(null)

    if (portalMode) {
      setRequiresMFA(false)
      return
    }

    API.get('/pts/checkmfa')
      .then((res) => {
        setRequiresMFA(res.status !== 200)
      })
      .catch(() => {
        setRequiresMFA(true)
      })
  }, [open, portalMode])

  // Reduced-minimum-dataset tenants can save a patient with just a name;
  // everything else is tracked as outstanding details on the booking. The
  // portal keeps the standard rules unless minimumDatasetPortal is on.
  const [reducedDataset, setReducedDataset] = useState(false)

  useEffect(() => {
    if (!open) return
    API.get(
      '/settings?select=patientTransportSettings.booking.minimumDataset%20patientTransportSettings.booking.minimumDatasetPortal',
    )
      .then((res) => {
        if (res.status !== 200) return
        const booking = res.data?.patientTransportSettings?.booking
        setReducedDataset(
          booking?.minimumDataset === 'reduced' &&
            (!portalMode || !!booking?.minimumDatasetPortal),
        )
      })
      .catch(() => {})
  }, [open, portalMode])

  useEffect(() => {
    if (open && existingPatient) {
      setFirstName(existingPatient.demographics?.firstName ?? '')
      setLastName(existingPatient.demographics?.lastName ?? '')
      setDOB(existingPatient.demographics?.dob ?? null)
      setGender(existingPatient.demographics?.gender ?? '')
      setGpSurgery(existingPatient.demographics?.gpSurgery ?? '')
      setNHSNumber(existingPatient.demographics?.nhsNumber ?? '')
      setHospitalNumbers(existingPatient.demographics?.hospitalNumbers ?? [])
      setAddressLine1(existingPatient.demographics?.addressLine1 ?? '')
      setAddressLine2(existingPatient.demographics?.addressLine2 ?? '')
      setTown(existingPatient.demographics?.town ?? '')
      setCity(existingPatient.demographics?.city ?? '')
      setPostCode(existingPatient.demographics?.postCode ?? '')
      setLocation(existingPatient.demographics?.location)
      setAddressMeta(existingPatient.demographics?.addressMeta)
      setEmail(existingPatient.demographics?.email ?? '')
      setPhoneNumber1(existingPatient.demographics?.phoneNumber1 ?? '')
      setPhoneNumber2(existingPatient.demographics?.phoneNumber2 ?? '')
      setNOKName(existingPatient.demographics?.nokName ?? '')
      setNokRelationship(existingPatient.demographics?.nokRelationship ?? '')
      setNokContactNumber(existingPatient.demographics?.nokContactNumber ?? '')
      setMobility(
        existingPatient.mobility ?? {
          category: '',
          isBariatric: false,
          mustTravelAlone: false,
          aids: [],
          heightCm: '',
          weightKg: '',
        },
      )
      setClinicalNotes(existingPatient.clinicalNotes ?? '')
      setRiskFlags(existingPatient.riskFlags ?? [])
      setAllergies(existingPatient.allergies ?? [])
      setMedications(existingPatient.medications ?? [])
      setPmhx(existingPatient.pmhx ?? [])
    }
  }, [existingPatient, open])

  const [showDuplicateNHSNoWarning, setShowDuplicateNHSNoWarning] =
    useState(false)
  const [showMissingDataWarning, setShowMissingDataWarning] = useState(false)
  const [noMatchingGeocode, setNoMatchingGeocode] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [duplicateWarnings, setDuplicateWarnings] = useState([])
  const [duplicateCheckBlocking, setDuplicateCheckBlocking] = useState(false)

  useEffect(() => {
    if (showDuplicateNHSNoWarning) {
      setShowDuplicateNHSNoWarning(false)
    }
    if (showMissingDataWarning) {
      setShowMissingDataWarning(false)
    }
    if (duplicateWarnings.length) {
      setDuplicateWarnings([])
      setDuplicateCheckBlocking(false)
    }
  }, [firstName, lastName, dob, nhsNumber, postCode, hospitalNumbers])

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setDOB(null)
    setGender('')
    setGpSurgery('')
    setNHSNumber('')
    setHospitalNumbers([])
    setAddressLine1('')
    setAddressLine2('')
    setTown('')
    setCity('')
    setPostCode('')
    setLocation(undefined)
    setAddressMeta(undefined)
    setEmail('')
    setPhoneNumber1('')
    setPhoneNumber2('')
    setNOKName('')
    setNokRelationship('')
    setNokContactNumber('')
    setMobility({
      category: '',
      isBariatric: false,
      mustTravelAlone: false,
      aids: [],
      heightCm: '',
      weightKg: '',
    })
    setClinicalNotes('')
    setRiskFlags([])
    setAllergies([])
    setMedications([])
    setPmhx([])
    setChangesMade({})
    setShowDuplicateNHSNoWarning(false)
    setShowMissingDataWarning(false)
    setDuplicateWarnings([])
    setDuplicateCheckBlocking(false)
    setAddressUpdated(false)
  }

  const [confirmNoMobility, setConfirmNoMobility] = useState(false)

  const handleSubmit = async ({
    skipDuplicateCheck = false,
    skipMobilityConfirm = false,
  } = {}) => {
    const anyAddressEntered = !!(
      addressLine1 ||
      addressLine2 ||
      town ||
      city ||
      postCode
    )
    // Reduced mode needs only a name; a DOB that is entered must be valid,
    // and a partly-entered address still needs line 1 + postcode so the
    // server's geocode doesn't fail on half an address
    const sufficientData = reducedDataset
      ? !!lastName &&
        !!firstName &&
        (!dob || moment(dob).isValid()) &&
        (!anyAddressEntered || (!!addressLine1 && !!postCode))
      : !!lastName &&
        moment(dob).isValid() &&
        !!firstName &&
        !!postCode &&
        !!addressLine1 &&
        !!mobility?.category

    if (!sufficientData) {
      setShowMissingDataWarning(true)
    } else if (reducedDataset && !mobility?.category && !skipMobilityConfirm) {
      // Mobility is deliberately not dropped silently — control room uses it
      // to pick the right vehicle (it stays an outstanding chip if skipped)
      setConfirmNoMobility(true)
    } else {
      setUploading(true)
      setSubmitError(null)
      const patient = {
        _id: existingPatient?._id ?? null,
        demographics: {
          firstName,
          lastName,
          dob,
          gender,
          gpSurgery,
          nhsNumber,
          hospitalNumbers: hospitalNumbers.map((x) => ({
            organisation: x.organisation?._id || x.organisation,
            mrn: x.mrn,
          })),
          addressLine1,
          addressLine2,
          town,
          city,
          postCode,
          location,
          addressMeta,
          email,
          phoneNumber1,
          phoneNumber2,
          nokName,
          nokRelationship,
          nokContactNumber,
        },
        // Height/weight are typed as text — send numbers (or null) so the
        // schema's Number cast never sees '' or junk
        mobility: {
          ...mobility,
          heightCm: Number.isFinite(Number(mobility?.heightCm))
            ? Number(mobility.heightCm) || null
            : null,
          weightKg: Number.isFinite(Number(mobility?.weightKg))
            ? Number(mobility.weightKg) || null
            : null,
        },
        clinicalNotes,
        riskFlags,
        allergies,
        medications,
        pmhx,
      }
      const afterEffect = (res) => {
        if (res.data?.errorCode === 'duplicate') {
          setShowDuplicateNHSNoWarning(true)
        } else if (res.data?.errorCode === 'noMatchingGeocode') {
          setNoMatchingGeocode(true)
        } else if (res.status === 200) {
          actionAfterSubmit({ ...patient, _id: res.data })
          onClose()
          resetForm()
        } else if (
          res.status === 403 &&
          res.data?.errorCode === 'notAuthorised'
        ) {
          setRequiresMFA(true)
        } else if (res.status >= 400) {
          setSubmitError({
            title: existingPatient
              ? 'Unable to update patient'
              : 'Unable to create patient',
            message:
              res.data?.error ||
              res.data?.message ||
              'The patient record could not be saved. Please try again.',
            statusCode: res.status,
          })
        }
      }

      try {
        if (existingPatient) {
          const res = await API.post(
            '/pts/edit-patient',
            {
              patient,
              changesMade,
            },
            { skipGlobalErrorHandler: true },
          )
          afterEffect(res)
        } else {
          if (!skipDuplicateCheck) {
            const duplicateRes = await API.post(
              portalMode
                ? '/pts/portal/patient-duplicates'
                : '/pts/patient-duplicates',
              patient,
              { skipGlobalErrorHandler: true },
            )

            if (duplicateRes.status === 200) {
              const warnings = duplicateRes.data?.warnings || []
              if (warnings.length > 0) {
                setDuplicateWarnings(warnings)
                setDuplicateCheckBlocking(
                  !!duplicateRes.data?.hasBlockingDuplicate,
                )
                return
              }
            } else if (duplicateRes.status >= 400) {
              afterEffect(duplicateRes)
              return
            }
          }

          const res = await API.post(
            portalMode ? '/pts/portal/create-patient' : '/pts/create-patient',
            patient,
            { skipGlobalErrorHandler: true },
          )
          afterEffect(res)
        }
      } catch (error) {
        const timedOut = error?.code === 'ECONNABORTED'
        setSubmitError({
          title: timedOut ? 'Patient creation timed out' : 'Unable to save patient',
          message: timedOut
            ? 'The request took too long to complete. Please check whether the patient was created before trying again.'
            : error?.response?.data?.error ||
              error?.response?.data?.message ||
              error?.message ||
              'The patient record could not be saved. Please try again.',
          statusCode: error?.response?.status,
        })
      } finally {
        setUploading(false)
      }
    }
  }

  return (
    <BigModal open={open} onClose={onClose}>
      <Box>
        <Stack gap={2.5}>
          {!noTitle && (
            <Box justifyContent="center" display="flex" width="100%">
              <Typography variant="title2">
                {existingPatient
                  ? 'Edit a Patient Record'
                  : 'Add a New Patient'}
              </Typography>
            </Box>
          )}
          {requiresMFA === null ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Loading />
            </Box>
          ) : requiresMFA ? (
            <Box>
              <MFALogin onSuccess={() => setRequiresMFA(false)} />
            </Box>
          ) : (
            <Stack gap={2.5}>
              <Demographics
                states={{
                  firstName,
                  setFirstName,
                  lastName,
                  setLastName,
                  dob,
                  setDOB,
                  gender,
                  setGender,
                  gpSurgery,
                  setGpSurgery,
                  nhsNumber,
                  setNHSNumber,
                  hospitalNumbers,
                  setHospitalNumbers,
                  addressLine1,
                  setAddressLine1,
                  addressLine2,
                  setAddressLine2,
                  town,
                  setTown,
                  city,
                  setCity,
                  postCode,
                  location,
                  setLocation,
                  addressMeta,
                  setAddressMeta,
                  setPostCode,
                  email,
                  setEmail,
                  phoneNumber1,
                  setPhoneNumber1,
                  phoneNumber2,
                  setPhoneNumber2,
                  nokName,
                  setNOKName,
                  nokRelationship,
                  setNokRelationship,
                  nokContactNumber,
                  setNokContactNumber,
                  setChangesMade,
                  existingPatient,
                  setAddressUpdated,
                }}
                showDuplicateNHSNoWarning={showDuplicateNHSNoWarning}
                showMissingDataWarning={showMissingDataWarning}
                portalOrganisation={portalOrganisation}
              />
              <Mobility
                mobility={mobility}
                setMobility={setMobility}
                setChangesMade={setChangesMade}
                existingPatient={existingPatient}
              />
              <Clinical
                clinicalNotes={clinicalNotes}
                setClinicalNotes={setClinicalNotes}
                riskFlags={riskFlags}
                setRiskFlags={setRiskFlags}
                allergies={allergies}
                setAllergies={setAllergies}
                medications={medications}
                setMedications={setMedications}
                pmhx={pmhx}
                setPmhx={setPmhx}
                existingPatient={existingPatient}
                setChangesMade={setChangesMade}
              />
              <SubmitForm
                addressUpdated={addressUpdated}
                handleSubmit={handleSubmit}
                showDuplicateNHSNoWarning={showDuplicateNHSNoWarning}
                showMissingDataWarning={showMissingDataWarning}
                reducedDataset={reducedDataset}
                uploading={uploading}
              />
            </Stack>
          )}
        </Stack>
      </Box>
      <ErrorModal
        open={noMatchingGeocode}
        noAutoClose
        onClose={() => {
          setNoMatchingGeocode(false)
        }}
        message="We couldn’t locate this address. Please check the postcode and address details to ensure crews can be routed correctly."
        title="Address not found"
        icon={TravelExploreTwoTone}
      />
      <ErrorModal
        open={!!submitError}
        noAutoClose
        onClose={() => setSubmitError(null)}
        message={submitError?.message}
        title={submitError?.title}
        statusCode={submitError?.statusCode}
      />
      <Dialog
        open={duplicateWarnings.length > 0}
        onClose={() => {
          if (!uploading) setDuplicateWarnings([])
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Possible duplicate patient
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity={duplicateCheckBlocking ? 'error' : 'warning'}>
              {duplicateCheckBlocking
                ? 'A patient with the same NHS number or hospital number already exists. Please use the existing patient record.'
                : 'One or more similar patients already exist for this organisation. Review them before creating a new patient.'}
            </Alert>
            {duplicateWarnings.map((warning, index) => (
              <Box
                key={`${warning.matchType}-${warning.patient?._id || index}`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography fontWeight={800}>
                  {[warning.patient?.firstName, warning.patient?.lastName]
                    .filter(Boolean)
                    .join(' ') || 'Existing patient'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {warning.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Patient ID: {warning.patient?.patientID || 'Unknown'} · DOB:{' '}
                  {warning.patient?.dob
                    ? moment(warning.patient.dob).format('DD/MM/YYYY')
                    : 'Unknown'}{' '}
                  · Postcode: {warning.patient?.postCode || 'Unknown'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDuplicateWarnings([])} disabled={uploading}>
            Review patient details
          </Button>
          {!duplicateCheckBlocking && (
            <Button
              variant="contained"
              color="warning"
              disabled={uploading}
              onClick={() => {
                setDuplicateWarnings([])
                handleSubmit({
                  skipDuplicateCheck: true,
                  skipMobilityConfirm: true,
                })
              }}
            >
              Create new patient anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmNoMobility}
        onClose={() => setConfirmNoMobility(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>No mobility recorded</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Without a mobility category the control room can't check vehicle
            suitability for this patient. It will stay flagged as outstanding
            on the booking until it's added.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmNoMobility(false)}>
            Add mobility
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmNoMobility(false)
              handleSubmit({ skipMobilityConfirm: true })
            }}
          >
            Save anyway
          </Button>
        </DialogActions>
      </Dialog>
    </BigModal>
  )
}

export default AddNewPatient
